import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useRef, useState } from 'react';
import { Box, Chip, FormHelperText, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { isEmailInvalid } from '../../helpers/general.helpers';
import { palette } from '../../theme';

export interface InviteAthletesDialogProps {
  isOpen: boolean;
  handleClose: any;
  handleSubmit: any;
  isLoading: boolean;
}

export default function InviteAthletesDialog(props: InviteAthletesDialogProps) {
  const [athletesForm, setAthletesForm] = useState<string[]>([]);
  const [athleteEmail, setAthleteEmail] = useState<string>('');
  const [athleteEmailError, setAthleteEmailError] = useState<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (athleteEmail.length > 1 && isEmailInvalid(athleteEmail)) {
        setAthleteEmailError('This email is invalid. Please use a valid email');
      } else {
        setAthleteEmailError('');
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [athleteEmail]);

  const closeModal = () => {
    setAthletesForm([]);
    setAthleteEmail('');
    setAthleteEmailError('');
    props.handleClose();
  };

  const submit = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // add athlete in text field to payload if text exists
    if (athleteEmail.length > 1) {
      if (isEmailInvalid(athleteEmail)) {
        setAthleteEmailError('This email is invalid. Please use a valid email');
        return;
      } else if (!athletesForm.includes(athleteEmail)) {
        athletesForm.push(athleteEmail);
      }
    }

    setAthleteEmailError('');
    const athletes = [...athletesForm];
    setAthletesForm([]);
    setAthleteEmail('');
    props.handleSubmit(athletes);
  };

  const addAthlete = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (isEmailInvalid(athleteEmail)) {
      setAthleteEmailError('This email is invalid. Please use a valid email');
    } else if (athletesForm.includes(athleteEmail)) {
      setAthleteEmailError('This athlete is already added');
    } else {
      setAthleteEmailError('');
      const updatedAthletes = [...athletesForm];
      updatedAthletes.push(athleteEmail);
      setAthletesForm(updatedAthletes);
      setAthleteEmail('');
    }
  };

  const removeAthlete = (email: string) => {
    const index = athletesForm.indexOf(email);
    const updatedAthletes = [...athletesForm];
    updatedAthletes.splice(index, 1);
    setAthletesForm(updatedAthletes);
  };

  const handleKeyDown = (evt: React.KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      addAthlete();
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { width: 520, maxWidth: 520 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Invite Athletes
        </Typography>
        <IconButton
          aria-label="close"
          onClick={closeModal}
          size="small"
          sx={{
            color: palette.text.secondary,
            '&:hover': { bgcolor: palette.grey[100] },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, color: palette.text.primary }}>
          Email Address
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            name="addAthlete"
            value={athleteEmail}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="name@example.com"
            onChange={(evt) => setAthleteEmail(evt.target.value)}
            onKeyDown={handleKeyDown}
            error={athleteEmailError.length > 1}
          />
          <IconButton
            aria-label="add athlete"
            onClick={addAthlete}
            sx={{
              bgcolor: palette.primary.light,
              color: palette.primary.main,
              borderRadius: '50%',
              flexShrink: 0,
              '&:hover': {
                bgcolor: palette.action.hover,
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>

        <FormHelperText error={athleteEmailError.length > 1} sx={{ mt: 0.5, mx: 0 }}>
          {athleteEmailError.length > 1
            ? athleteEmailError
            : 'Enter an email and press + to add. You can invite multiple athletes at once.'}
        </FormHelperText>

        {athletesForm.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {athletesForm.map((item: string) => (
              <Chip
                key={item}
                label={item}
                onDelete={() => removeAthlete(item)}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: palette.divider,
                  color: palette.text.primary,
                  '&:hover': { bgcolor: palette.primary.light },
                }}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
        <Button
          onClick={closeModal}
          variant="text"
          sx={{
            color: palette.text.secondary,
            '&:hover': { bgcolor: palette.grey[100] },
          }}
        >
          Cancel
        </Button>
        <Button onClick={submit} disabled={props.isLoading} variant="contained" color="primary">
          Send Invites
        </Button>
      </DialogActions>
    </Dialog>
  );
}
